import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getMessageMetadata, listMessages } from "../gmail/api";
import { ParsedMessageMetadata } from "../gmail/types";
import { scoreEmail } from "../scoring/rules";
import Loading from "../ui/Loading";
import ErrorBanner from "../ui/ErrorBanner";

type RiskBand = "low" | "moderate" | "elevated" | "high";

function getRiskBand(score: number): RiskBand {
  if (score >= 76) return "high";
  if (score >= 51) return "elevated";
  if (score >= 26) return "moderate";
  return "low";
}

export default function InboxPage() {
  const { token } = useAuth();
  const [messages, setMessages] = useState<ParsedMessageMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeQuery, setActiveQuery] = useState<string>("");
  const [pageTokens, setPageTokens] = useState<Record<string, string | undefined>>({});
  const [showLegend, setShowLegend] = useState(false);
  const [sortByRisk, setSortByRisk] = useState(false);

  const isGmailQuery = (term: string) => /(from:|subject:|has:|label:)/i.test(term);

  const fetchMessages = async (query: string, pageToken?: string, append = false) => {
    if (!token) return;
    const setLoadingFn = append ? setLoadingMore : setLoading;
    setLoadingFn(true);
    setError(undefined);
    try {
      const { messages: ids, nextPageToken } = await listMessages(token, { pageToken, q: query || undefined });
      const results = await Promise.all(ids.map((item) => getMessageMetadata(token, item.id)));
      setMessages((prev) => (append ? [...prev, ...results] : results));
      setPageTokens((prev) => ({ ...prev, [query]: nextPageToken }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load messages");
    } finally {
      setLoadingFn(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchMessages("");
  }, [token]);

  const scoredMessages = useMemo(
    () =>
      messages.map((msg, index) => ({
        message: msg,
        risk: scoreEmail({
          from: msg.from,
          replyTo: msg.replyTo,
          subject: msg.subject,
          snippet: msg.snippet,
          headers: msg.headers,
          links: [],
          attachments: []
        }),
        originalIndex: index
      })),
    [messages]
  );

  const displayedMessages = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered =
      !isGmailQuery(activeQuery) && term
        ? scoredMessages.filter(({ message: msg }) =>
            [msg.from ?? "", msg.subject ?? "", msg.snippet ?? ""].some((field) => field.toLowerCase().includes(term))
          )
        : scoredMessages;
    if (!sortByRisk) return filtered;

    return [...filtered].sort((a, b) => {
      if (b.risk.score === a.risk.score) return a.originalIndex - b.originalIndex;
      return b.risk.score - a.risk.score;
    });
  }, [activeQuery, scoredMessages, searchTerm, sortByRisk]);

  const handleSearch = () => {
    const term = searchTerm.trim();
    if (!term) {
      setActiveQuery("");
      fetchMessages("");
      return;
    }
    if (isGmailQuery(term)) {
      setActiveQuery(term);
      fetchMessages(term);
    } else {
      setActiveQuery("");
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    setActiveQuery("");
    fetchMessages("");
  };

  const handleLoadOlder = () => {
    const tokenForQuery = pageTokens[activeQuery];
    if (!tokenForQuery) return;
    fetchMessages(activeQuery, tokenForQuery, true);
  };

  if (!token) {
    return (
      <div className="card">
        <h2>Connect your Gmail</h2>
        <p>Sign in on the home page to load your recent messages. Nothing leaves your browser.</p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="inbox-controls">
        <div>
          <h2>Recent messages</h2>
          <p className="list-meta">Search locally or use Gmail queries like from:, subject:, has:, or label:.</p>
          <p className="list-meta subtle-note">
            Messages are analyzed locally in your browser and assigned a risk score based on sender signals, links, and common scam
            patterns.
          </p>
        </div>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search sender, subject, or Gmail query…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="search-actions">
            <button className="button-primary" onClick={handleSearch} disabled={!searchTerm && !activeQuery}>
              Search
            </button>
            <button className="button-secondary" onClick={handleClear}>
              Clear
            </button>
          </div>
        </div>
        <div className="list-extras">
          <label className="toggle">
            <input
              type="checkbox"
              checked={sortByRisk}
              onChange={(e) => setSortByRisk(e.target.checked)}
            />
            <span>Sort by risk (high to low)</span>
          </label>
          <button className="button-secondary legend-toggle" onClick={() => setShowLegend((prev) => !prev)}>
            {showLegend ? "Hide legend" : "What do these colors mean?"}
          </button>
        </div>
        {showLegend && (
          <div className="risk-legend">
            <div className="legend-row">
              <span className="legend-swatch risk-low" aria-hidden />
              <span>Low (0–25)</span>
            </div>
            <div className="legend-row">
              <span className="legend-swatch risk-moderate" aria-hidden />
              <span>Moderate (26–50)</span>
            </div>
            <div className="legend-row">
              <span className="legend-swatch risk-elevated" aria-hidden />
              <span>Elevated (51–75)</span>
            </div>
            <div className="legend-row">
              <span className="legend-swatch risk-high" aria-hidden />
              <span>High (76–100)</span>
            </div>
          </div>
        )}
      </div>

      {loading && <Loading label="Loading recent messages" />}
      {error && <ErrorBanner message={error} />}
      {!loading && !error && (
        <div className="grid">
          {displayedMessages.map(({ message: msg, risk }) => {
            const band = getRiskBand(risk.score);
            return (
              <div key={msg.id} className={`list-item card message-card risk-${band}`}>
                <div className="message-main">
                  <div className="message-title-row">
                    <strong>{msg.subject || "(No subject)"}</strong>
                    <span className="score-chip" title="Higher scores indicate higher phishing risk.">
                      {risk.score} / 100
                    </span>
                  </div>
                  <div className="list-meta">From: {msg.from ?? "Unknown"}</div>
                  <div className="list-meta">Snippet: {msg.snippet}</div>
                </div>
                <div className="message-actions">
                  <div className={`risk-pill risk-${band}`} title={"Higher scores indicate higher phishing risk."}>
                    <span className="risk-dot" aria-hidden />
                    <span className="risk-label">{band.charAt(0).toUpperCase() + band.slice(1)}</span>
                  </div>
                  <Link to={`/messages/${msg.id}`} className="button-secondary">
                    View
                  </Link>
                </div>
              </div>
            );
          })}
          {messages.length === 0 && <p>No messages loaded yet.</p>}
        </div>
      )}

      <div className="load-older">
        <button
          className="button-secondary"
          onClick={handleLoadOlder}
          disabled={loading || loadingMore || !pageTokens[activeQuery]}
        >
          {loadingMore ? "Loading…" : "Load older"}
        </button>
        {!pageTokens[activeQuery] && <span className="list-meta">No more messages to load.</span>}
      </div>
    </div>
  );
}
